import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import React, { useState } from "react";
import { QUEUE_LABELS, QueueName } from "types/queue";

const useStyles = makeStyles((theme: any) => ({
  dialogTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: "#1d1030",
    color: "#fff",
  },
  dialogContent: {
    padding: "24px !important",
  },
  formField: {
    marginBottom: 20,
  },
  warningText: {
    color: "#d32f2f",
    fontSize: 13,
    marginTop: 16,
    padding: 12,
    backgroundColor: "#ffebee",
    borderRadius: 4,
  },
}));

interface CleanQueueDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (
    queueName: QueueName,
    state: "completed" | "failed",
    gracePeriodMs: number
  ) => void;
  queueName: QueueName | null;
  loading?: boolean;
}

const CleanQueueDialog: React.FC<CleanQueueDialogProps> = ({
  open,
  onClose,
  onConfirm,
  queueName,
  loading = false,
}) => {
  const classes = useStyles();
  const [state, setState] = useState<"completed" | "failed">("failed");
  const [gracePeriodHours, setGracePeriodHours] = useState<number>(1);

  const handleConfirm = () => {
    if (queueName) {
      const gracePeriodMs = gracePeriodHours * 60 * 60 * 1000;
      onConfirm(queueName, state, gracePeriodMs);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle className={classes.dialogTitle}>
        <Typography variant="h6">
          Clean Queue - {queueName ? QUEUE_LABELS[queueName] : ""}
        </Typography>
        <IconButton onClick={onClose} sx={{ color: "#fff" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        <Box className={classes.formField}>
          <FormControl fullWidth>
            <InputLabel>State to Clean</InputLabel>
            <Select
              value={state}
              label="State to Clean"
              onChange={(e) =>
                setState(e.target.value as "completed" | "failed")
              }
            >
              <MenuItem value="completed">Completed Jobs</MenuItem>
              <MenuItem value="failed">Failed Jobs</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Box className={classes.formField}>
          <TextField
            fullWidth
            type="number"
            label="Grace Period (hours)"
            value={gracePeriodHours}
            onChange={(e) => setGracePeriodHours(Number(e.target.value))}
            helperText="Jobs newer than this will be kept"
            inputProps={{ min: 0 }}
          />
        </Box>

        <Typography className={classes.warningText}>
          ⚠️ This action will permanently remove {state} jobs older than{" "}
          {gracePeriodHours} hour(s) from the queue. This cannot be undone.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ padding: 2 }}>
        <Button onClick={onClose} variant="outlined" disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="error"
          disabled={loading}
        >
          {loading ? "Cleaning..." : "Clean Queue"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// PauseQueueDialog Component
interface PauseQueueDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  queueName: QueueName | null;
  isPaused: boolean;
  loading?: boolean;
}

export const PauseQueueDialog: React.FC<PauseQueueDialogProps> = ({
  open,
  onClose,
  onConfirm,
  queueName,
  isPaused,
  loading = false,
}) => {
  const classes = useStyles();
  const action = isPaused ? "Resume" : "Pause";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle className={classes.dialogTitle}>
        <Typography variant="h6">
          {action} Queue - {queueName ? QUEUE_LABELS[queueName] : ""}
        </Typography>
        <IconButton onClick={onClose} sx={{ color: "#fff" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        <Typography variant="body1" sx={{ mb: 2 }}>
          {isPaused
            ? `Are you sure you want to resume the ${
                queueName ? QUEUE_LABELS[queueName] : ""
              } queue?`
            : `Are you sure you want to pause the ${
                queueName ? QUEUE_LABELS[queueName] : ""
              } queue?`}
        </Typography>

        <Typography
          className={classes.warningText}
          sx={{
            backgroundColor: isPaused
              ? "#e8f5e9 !important"
              : "#fff3e0 !important",
            color: isPaused ? "#2e7d32 !important" : "#e65100 !important",
          }}
        >
          {isPaused
            ? "✓ Resuming the queue will start processing pending jobs again."
            : "⚠️ Pausing the queue will stop processing new jobs. Active jobs will continue until completion."}
        </Typography>
      </DialogContent>

      <DialogActions sx={{ padding: 2 }}>
        <Button onClick={onClose} variant="outlined" disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color={isPaused ? "success" : "warning"}
          disabled={loading}
        >
          {loading ? `${action}ing...` : `${action} Queue`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CleanQueueDialog;
